import * as R from 'ramda';

import WorkOrderIcon from '@atom/components/common/svgIcons/work';
import {
  AtomMap,
  DEFAULT_MAP_CENTER,
  getActiveAssetIconFromSchemaMarkerId,
  getGoogleShapeCenter,
  getInactiveAssetIconFromSchemaMarkerId,
  getWorkOrderSvg,
  isPoint,
} from '@atom/map';
import { LocationGeometry } from '@atom/types/geojson';
import { EditTarget } from '@atom/types/map';
import { PolicyAction } from '@atom/types/policy';
import { WorkOrderDetailType } from '@atom/types/work';
import { hasAccess } from '@atom/utilities/accessUtilities';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import { getColorFromColorId } from '@atom/utilities/colorUtilities';

import LocationLinkControls from './LocationLinkControls';
import useWorkOrderLocationSync from './useWorkOrderLocationSync';

const WORK_DETAIL_ZOOM = 17;

type WorkOrderDetailMapProps = {
  workOrderDetail: WorkOrderDetailType;
  refetch: () => void;
  loading: boolean;
};

const WorkOrderDetailMap = ({
  workOrderDetail,
  refetch,
  loading,
}: WorkOrderDetailMapProps) => {
  const {
    id,
    statusId,
    location,
    isClosed,
    inventoryAssetId,
    inventoryAssetName,
    inventoryAssetLocation,
    inventoryAssetMarkerId,
    inventoryAssetAddress,
    inventoryAssetActions,
  } = workOrderDetail;

  const {
    updateWorkLocation,
    updateAssetLocation,
    loading: syncLoading,
  } = useWorkOrderLocationSync(id, inventoryAssetId, refetch);

  const center = getGoogleShapeCenter(location) ?? DEFAULT_MAP_CENTER;

  const canEditWorkOrder = !isClosed && hasRolePermissions(ROLE_SETS.INSPECTOR);
  const canEditAsset =
    !isClosed && hasAccess(PolicyAction.UPDATE_LOCATION, inventoryAssetActions);

  const workOrderTarget: EditTarget = {
    id,
    name: workOrderDetail.name ?? '',
    type: 'workOrder',
    icon: <img src={getWorkOrderSvg(statusId, false)} alt="" />,
    activeIcon: <img src={getWorkOrderSvg(statusId, true)} alt="" />,
    listIcon: <WorkOrderIcon status={+statusId} />,
    location: location ?? null,
    canEdit: canEditWorkOrder,
  };

  const hasAsset = !R.isEmpty(inventoryAssetId);

  const assetTarget: EditTarget = {
    id: inventoryAssetId,
    name: inventoryAssetName ?? '',
    type: 'asset',
    address: inventoryAssetAddress,
    icon: (
      <img
        src={getInactiveAssetIconFromSchemaMarkerId(inventoryAssetMarkerId)}
        alt=""
      />
    ),
    activeIcon: (
      <img
        src={getActiveAssetIconFromSchemaMarkerId(inventoryAssetMarkerId)}
        alt=""
      />
    ),
    color: getColorFromColorId(inventoryAssetMarkerId),
    location: inventoryAssetLocation ?? null,
    canEdit: canEditAsset,
  };

  const targets: EditTarget[] = [
    workOrderTarget,
    ...(hasAsset ? [assetTarget] : []),
  ];

  const handleSave = (newLocation: LocationGeometry, targetId: string) => {
    if (targetId === id) {
      updateWorkLocation(newLocation);
    } else {
      updateAssetLocation(newLocation);
    }
  };

  const canSyncLocations = hasAsset && isPoint(inventoryAssetLocation);

  return (
    <AtomMap
      initialViewState={{ center, zoom: WORK_DETAIL_ZOOM }}
      loading={loading || syncLoading}
      search="editable"
      editing={{
        targets,
        onSave: handleSave,
      }}
      customToolbars={
        <LocationLinkControls
          canSyncWorkToInventory={canSyncLocations && canEditWorkOrder}
          canSyncInventoryToWork={canSyncLocations && canEditAsset}
          onSyncWorkToInventory={() =>
            updateWorkLocation(inventoryAssetLocation)
          }
          onSyncInventoryToWork={() => updateAssetLocation(location)}
        />
      }
    />
  );
};

export default WorkOrderDetailMap;
